import {
  INDIA_CURRENCY_FORMAT,
  US_CURRENCY_FORMAT,
} from "../../../constants";
import { formatCurrency, handleBlendedFxRates } from "../../../libs/utils";

export const DEFAULT_USD_AMOUNT = 1000;
const FALLBACK_BASE_RATE = 95;
const FALLBACK_PROMO_RATE = 105;

export const toNumber = (value) => {
  const parsed = parseFloat(value?.toString().replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatUsd = (value, hideDecimals = true) =>
  `$${formatCurrency(`${toNumber(value)}`, US_CURRENCY_FORMAT, hideDecimals)}`;

export const formatInr = (value, hideDecimals = true) =>
  `₹${formatCurrency(`${toNumber(value)}`, INDIA_CURRENCY_FORMAT, hideDecimals)}`;

export const formatRate = (value) =>
  `₹${toNumber(value || FALLBACK_BASE_RATE).toFixed(2).replace(/\.00$/, "")}`;

export const getRateViewModel = (exchangeData = {}, amount = DEFAULT_USD_AMOUNT) => {
  const normalizedAmount = Math.max(toNumber(amount), 0);
  const baseRate = toNumber(exchangeData.base_fx) || FALLBACK_BASE_RATE;
  const promoRate = toNumber(exchangeData.promo_fx) || FALLBACK_PROMO_RATE;
  const hasPromo = exchangeData.is_promo_applicable !== false;
  const transferFee = toNumber(exchangeData.applicable_transaction_fee_data?.value);
  const transferFeeDisplay =
    exchangeData.applicable_transaction_fee_data?.display_value ||
    (transferFee ? formatUsd(transferFee, false) : "$0.00");
  const displayBaseRate = exchangeData.display_base_fx || formatRate(baseRate);
  const displayPromoRate = exchangeData.display_promo_fx || formatRate(promoRate);

  if (!normalizedAmount) {
    return {
      amount: 0,
      baseRate,
      promoRate,
      hasPromo,
      displayBaseRate,
      displayPromoRate,
      transferFee,
      transferFeeDisplay,
      recipientAmount: 0,
      blendedRate: hasPromo ? promoRate : baseRate,
      breakdown: [],
      promoBreakdown: null,
      standardBreakdown: null,
    };
  }

  const { breakdown = [] } = handleBlendedFxRates(normalizedAmount, exchangeData, "USD");
  const usableBreakdown = breakdown.length
    ? breakdown
    : [
        {
          source_amount: hasPromo ? Math.min(100, normalizedAmount) : normalizedAmount,
          destination_amount: (hasPromo ? Math.min(100, normalizedAmount) : normalizedAmount) * (hasPromo ? promoRate : baseRate),
          effective_fx_rate: hasPromo ? promoRate : baseRate,
          display_effective_rate: hasPromo ? displayPromoRate : displayBaseRate,
          promotional_applicable: hasPromo,
        },
        ...(hasPromo && normalizedAmount > 100
          ? [
              {
                source_amount: normalizedAmount - 100,
                destination_amount: (normalizedAmount - 100) * baseRate,
                effective_fx_rate: baseRate,
                display_effective_rate: displayBaseRate,
                promotional_applicable: false,
              },
            ]
          : []),
      ];

  const recipientAmount = usableBreakdown.reduce(
    (sum, item) => sum + toNumber(item.destination_amount),
    0
  );
  const blendedRate = recipientAmount / amount;
  const promoBreakdown = usableBreakdown.find((item) => item.promotional_applicable);
  const standardBreakdown = usableBreakdown.find(
    (item) => !item.promotional_applicable,
  );

  return {
    amount: normalizedAmount,
    baseRate,
    promoRate,
    hasPromo,
    displayBaseRate,
    displayPromoRate,
    transferFee,
    transferFeeDisplay,
    recipientAmount,
    blendedRate,
    breakdown: usableBreakdown,
    promoBreakdown,
    standardBreakdown,
  };
};

export const getUsdAmountForRecipient = (exchangeData = {}, recipientAmount = 0) => {
  const targetAmount = Math.max(toNumber(recipientAmount), 0);

  if (!targetAmount) {
    return 0;
  }

  let low = 0;
  let high = Math.max(targetAmount / (toNumber(exchangeData.promo_fx) || FALLBACK_PROMO_RATE), 1);

  while (getRateViewModel(exchangeData, high).recipientAmount < targetAmount) {
    high *= 2;
  }

  for (let index = 0; index < 32; index += 1) {
    const mid = (low + high) / 2;
    const convertedAmount = getRateViewModel(exchangeData, mid).recipientAmount;

    if (convertedAmount < targetAmount) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return Math.round(high * 100) / 100;
};
